import type { AnalyticsInterval } from "@lafayette/schemas";

// The arithmetic of zooming in Explore: which window a bar, a drag or the navigator stands for, how a window is moved and resized, how a date of a
// time zone becomes an instant, and how a window is written in words. Pure, so a test proves each rule. A window is `{ from, to }` as ISO strings
// (`[from, to)`); the server accepts a zoom that lies inside the range, ends after it starts and lasts at least one hour.

export type Zoom = Readonly<{ from: string; to: string }>;

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;
/** The shortest window the server accepts. */
const MIN_ZOOM_MS = HOUR_MS;
/** What the server allows for a bucket (`packages/domain/src/customer-analytics-metrics.ts`): hours up to 7 days, days up to 400 days. */
const INTERVAL_MAX_SPAN_MS: Readonly<Record<AnalyticsInterval, number>> = {
  hour: 7 * DAY_MS,
  day: 400 * DAY_MS,
  week: Number.POSITIVE_INFINITY,
};

export const intervalAllowed = (interval: AnalyticsInterval, from: string, to: string): boolean =>
  Date.parse(to) - Date.parse(from) <= INTERVAL_MAX_SPAN_MS[interval];

const ms = (iso: string) => Date.parse(iso);
const clamp = (value: number, low: number, high: number) => Math.min(Math.max(value, low), high);

type Bound = Readonly<{ from: string; to: string }>;

/**
 * The window of the buckets `first` to `last` (both inclusive, in any order) of a series whose bucket starts are `starts`. The last bucket ends at
 * `bound.to` (the end of the window of the series). The window is cut to the bound; a window shorter than one hour grows backward to one hour.
 * `null` when the bound itself is shorter than an hour: there is nothing to zoom into.
 */
export function zoomOfBuckets(
  starts: readonly string[],
  first: number,
  last: number,
  bound: Bound,
): Zoom | null {
  const low = Math.min(first, last);
  const high = Math.max(first, last);
  const start = starts[low];
  if (start === undefined) return null;
  const nextStart = starts[high + 1];
  const from = Math.max(ms(start), ms(bound.from));
  const to = Math.min(nextStart === undefined ? ms(bound.to) : ms(nextStart), ms(bound.to));
  const widened = to - from < MIN_ZOOM_MS ? Math.max(to - MIN_ZOOM_MS, ms(bound.from)) : from;
  if (to - widened < MIN_ZOOM_MS) return null;
  return { from: new Date(widened).toISOString(), to: new Date(to).toISOString() };
}

/** The index of the bucket that holds the horizontal position `x` of a plot `width` wide with `count` buckets. */
export function bucketAt(x: number, width: number, count: number): number {
  if (count <= 0 || width <= 0) return 0;
  return clamp(Math.floor((x / width) * count), 0, count - 1);
}

const lastIndexWhere = (items: readonly string[], test: (item: string) => boolean): number => {
  for (let index = items.length - 1; index >= 0; index -= 1)
    if (test(items[index] as string)) return index;
  return -1;
};

/** The window as the buckets of the navigator: the first and the last bucket that it covers. A whole range is every bucket. */
export function windowIndices(
  starts: readonly string[],
  zoom: Zoom | null,
): readonly [number, number] {
  const last = Math.max(starts.length - 1, 0);
  if (zoom === null) return [0, last];
  const first = lastIndexWhere(starts, (start) => ms(start) <= ms(zoom.from));
  const end = lastIndexWhere(starts, (start) => ms(start) < ms(zoom.to));
  return [clamp(first, 0, last), clamp(end, 0, last)];
}

export type WindowEdit = "earlier" | "later" | "narrower" | "wider";

/** One step of the navigator buttons: the window moves by half its width, or grows or shrinks by a quarter on each side. Stays inside the buckets. */
export function editWindow(
  edit: WindowEdit,
  indices: readonly [number, number],
  count: number,
): readonly [number, number] {
  const [first, last] = indices;
  const width = last - first + 1;
  const step = Math.max(1, Math.round(width / 2));
  const grow = Math.max(1, Math.round(width / 4));
  const highest = Math.max(count - 1, 0);
  if (edit === "earlier") {
    const shift = Math.min(step, first);
    return [first - shift, last - shift];
  }
  if (edit === "later") {
    const shift = Math.min(step, highest - last);
    return [first + shift, last + shift];
  }
  if (edit === "wider") return [Math.max(first - grow, 0), Math.min(last + grow, highest)];
  const narrowed: [number, number] = [first + grow, last - grow];
  return narrowed[0] > narrowed[1] ? [first, first] : narrowed;
}

// --- time zones ---------------------------------------------------------------------------------------------------------------------

/** The offset of a zone from UTC at an instant, in milliseconds (positive east of Greenwich). */
export function zoneOffsetMs(instant: number, tz: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(instant));
  const read = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  const asUtc = Date.UTC(
    read("year"),
    read("month") - 1,
    read("day"),
    read("hour"),
    read("minute"),
    read("second"),
  );
  return asUtc - Math.floor(instant / 1000) * 1000;
}

/** The instant at which the day `YYYY-MM-DD` begins in the zone `tz`. `null` for a text that is not a date. */
export function startOfZonedDay(day: string, tz: string): string | null {
  const found = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(day);
  if (found === null) return null;
  const guess = Date.UTC(Number(found[1]), Number(found[2]) - 1, Number(found[3]));
  if (Number.isNaN(guess)) return null;
  // The offset at the guess may differ from the offset at midnight itself (a change of daylight saving that day): one refinement settles it.
  const first = guess - zoneOffsetMs(guess, tz);
  return new Date(guess - zoneOffsetMs(first, tz)).toISOString();
}

/** The day `YYYY-MM-DD` that an instant falls on in the zone. */
export function zonedDay(iso: string, tz: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso));
  return parts;
}

// --- words --------------------------------------------------------------------------------------------------------------------------

const SHORT_DATE = (tz: string) =>
  new Intl.DateTimeFormat("en-GB", { timeZone: tz, day: "numeric", month: "short" });
const SHORT_TIME = (tz: string) =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? "" : "s"}`;

/** The length of a window as a phrase: "3 weeks", "1 day", "6 hours". Rounds to the nearest whole unit of the biggest that fits. */
export function spanPhrase(zoom: Zoom): string {
  const span = ms(zoom.to) - ms(zoom.from);
  if (span >= 14 * DAY_MS) return plural(Math.round(span / (7 * DAY_MS)), "week");
  if (span >= DAY_MS) return plural(Math.round(span / DAY_MS), "day");
  return plural(Math.max(1, Math.round(span / HOUR_MS)), "hour");
}

/** The chip: "6 Oct – 27 Oct · 3 weeks". A window inside one day shows the hours: "22 Oct, 08:00 – 14:00 · 6 hours". */
export function zoomChipText(zoom: Zoom, tz: string): string {
  const startText = SHORT_DATE(tz).format(new Date(zoom.from));
  const lastInstant = new Date(ms(zoom.to) - 1);
  const endText = SHORT_DATE(tz).format(lastInstant);
  if (startText === endText && ms(zoom.to) - ms(zoom.from) < DAY_MS) {
    const time = SHORT_TIME(tz);
    return `${startText}, ${time.format(new Date(zoom.from))} – ${time.format(new Date(zoom.to))} · ${spanPhrase(zoom)}`;
  }
  return `${startText} – ${endText} · ${spanPhrase(zoom)}`;
}

/** The same window for a sentence that a screen reader reads: no dash. */
export const zoomSpoken = (zoom: Zoom, tz: string): string =>
  zoomChipText(zoom, tz).replace(" – ", " to ");

/** The label of one bucket for the tooltip and the axis: "22 Oct", "22 Oct, 08:00", and "Week of 20 Oct". */
export function bucketTitle(iso: string, tz: string, interval: AnalyticsInterval): string {
  const date = SHORT_DATE(tz).format(new Date(iso));
  if (interval === "hour") return `${date}, ${SHORT_TIME(tz).format(new Date(iso))}`;
  return interval === "week" ? `Week of ${date}` : date;
}

/** The word of what a click on a bar of this interval does. */
export const ZOOM_HINT: Readonly<Record<AnalyticsInterval, string>> = {
  week: "Click to zoom into this week",
  day: "Click to zoom into this day",
  hour: "Click to see the visits in this hour",
};
